import "@fullcalendar/react/dist/vdom";
import { defaultVariantColorsResolver, MantineProvider, Stack, VariantColorsResolver } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import { ModalsProvider } from "@mantine/modals";
import { McDesignProvider } from "@multiconsult-group/core";
import "@multiconsult-group/core/styles.css";
import { PropsWithChildren, useEffect } from "react";
import { Provider } from "react-redux";
import { HashRouter, Route, useHistory, useLocation } from "react-router-dom";
import { useGetProjectProjectIdQuery } from "../openapi";
import App from "./app/App";
import { getProjectIdFromUrl } from "./app/utils";
import { FailedAlert, MCLoader } from "./components";
import { selectIsUserDev } from "./global/globalSlice";
import { useAppSelector } from "./hooks";
import { LanguageProvider, useTranslation } from "./i18n";
import { store } from "./store";
import ErrorBoundary from "./util/ErrorBoundary";
import { Notifications } from "@mantine/notifications";

const variantColorResolver: VariantColorsResolver = (input) => {
  const base = defaultVariantColorsResolver(input);

  if (input.variant === "secondary-filled") {
    return {
      background: "var(--mantine-color-gray-1)",
      hover: "var(--mantine-color-gray-2)",
      active: "var(--mantine-color-gray-7)",
      color: "var(--mantine-color-warmgray-8)",
      border: "0",
    };
  }

  if (input.variant === "secondary-subtle") {
    return {
      background: "transparent",
      hover: "var(--mantine-color-gray-1)",
      active: "var(--mantine-color-warmgray-5)",
      color: "var(--mantine-color-warmgray-7)",
      border: "1px solid transparent",
    };
  }

  if (input.variant === "danger-filled") {
    return {
      background: "#db5353", // base
      hover: "#ed6f6f", // hover color from .btn-danger:hover
      active: "#c84242", // slightly darker for active state
      color: "#ffffff",
      border: "0",
    };
  }

  return base;
};
const MainApp = () => {
  return (
    <McDesignProvider>
      <MantineProvider
        theme={{
          colors: {
            "mc-primary": [
              "#f0f6fb",
              "#ddebf5",
              "#c2dcef",
              "#a5cce6",
              "#87b9dc",
              "#6aa4d0",
              "#4782b8",
              "#3c6fa0",
              "#315a82",
              "#223d57",
            ],
          },
          primaryColor: "mc-primary",
          primaryShade: 6,
          variantColorResolver,
          components: {
            Button: {
              defaultProps: {
                variant: "filled",
              },
            },
            Switch: {
              defaultProps: {
                withThumbIndicator: false,
              },
            },
          },
        }}
      >
        <Provider store={store}>
          <ErrorBoundary isGlobal={true} identifier="mainApp">
            <LanguageProviderGate>
              <HashRouter>
                <Route exact path="/">
                  <AppWrapper />
                </Route>
                <Route exact path="">
                  <AppWrapper />
                </Route>
              </HashRouter>
            </LanguageProviderGate>
          </ErrorBoundary>
        </Provider>
      </MantineProvider>
    </McDesignProvider>
  );
};

export default MainApp;

const LanguageProviderGate: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const isUserDev = useAppSelector(selectIsUserDev);

  return (
    <LanguageProvider languageSelectionEnabled={isUserDev}>
      <ModalsProvider>
        <Notifications />
        {children}
      </ModalsProvider>
    </LanguageProvider>
  );
};

const AppWrapper = () => {
  const t = useTranslation();
  const history = useHistory();
  const location = useLocation(); // <-- subscribe to URL changes
  const currentPath = location.pathname;
  const projectId = getProjectIdFromUrl(currentPath);
  const projectIdIsAMongoId = projectId != null && /^[0-9a-fA-F]{24}$/.test(projectId);
  const projectIdIsMaconomyProjectNo = projectId != null && /^\d{8}$/.test(projectId);

  const {
    data: projectIdFromServer,
    error: projectIdError,
    isLoading,
  } = useGetProjectProjectIdQuery(
    {
      projectNo: parseInt(projectId),
    },
    { skip: !projectIdIsMaconomyProjectNo },
  );

  useEffect(() => {
    // Kör endast om URL:en just nu innehåller Maconomy-projektNr och vi har svar
    if (!projectIdIsMaconomyProjectNo) return;
    if (!projectIdFromServer) return;

    // Sanity: vi förväntar oss ett MongoId tillbaka
    const looksLikeMongoId = /^[0-9a-fA-F]{24}$/.test(projectIdFromServer);
    if (!looksLikeMongoId) return;

    // Byt endast det path-segment som är projectId efter /project/
    const segRegex = new RegExp(`(^|/)(project)/${projectId}(?=/|$)`);
    const newPath = currentPath.replace(segRegex, (_m, p1, p2) => `${p1}${p2}/${projectIdFromServer}`);

    if (newPath !== currentPath) {
      history.replace(newPath);
    }
    // Efter replace kommer useLocation() ge nytt pathname -> denna effekt avfyras igen,
    // men guardarna ovan stoppar ny replace (nu är det inte Maconomy längre), så ingen loop.
  }, [projectIdIsMaconomyProjectNo, projectIdFromServer, projectId, currentPath, history]);

  return (
    <Stack gap={0} h="100vh">
      {(projectId == null || projectIdIsAMongoId) && <App />}
      {projectId != null && !projectIdIsAMongoId && !projectIdIsMaconomyProjectNo && <p>{t("app.invalidProjectId")}</p>}
      {projectIdIsMaconomyProjectNo && isLoading && (
        <p>
          <MCLoader />
          {t("app.loadingProjectInfo")}
        </p>
      )}
      {projectIdError != null && (
        <FailedAlert errorMessage={t("app.projectInfoError", { projectId, error: JSON.stringify(projectIdError) })} />
      )}
    </Stack>
  );
};
